<template>
  <div>
    <video
      ref="videoPlayerRef"
      class="video-js h-full w-full rounded-2"
      :class="{
        '!pt-0 !w-full !h-full': ignoreAspectRatio,
        '!py-0': options?.maxHeight && !ignoreAspectRatio,
      }"
      :style="{
        height: options?.maxHeight && !ignoreAspectRatio ? `${options.maxHeight}px!important` : undefined,
      }"
      v-on="preventPointerEvents ? { pointerdown: preventDefault, pointerup: preventDefault } : {}"
    ></video>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, type Ref, type ComputedRef } from 'vue'

import { loadScriptStyle } from '@lib/util/dom'
import type { VideoPlayerType } from './CVideo.types.ts'
import type { BackgroundSize } from '@lib/types/background'

const props = defineProps<{
  currentTime?: number
  ignoreAspectRatio?: boolean
  preventPointerEvents?: boolean // Useful for swiper interactions to allow slide change
  options?: {
    sources?: { src: string; type?: string }[]
    fluid?: boolean
    preload?: string
    controls?: boolean
    responsive?: boolean
    maxHeight?: number
    liveui?: boolean
    enableSmoothSeeking?: boolean
    autoplay?: boolean
    poster?: string
    posterBackgroundSize?: BackgroundSize
  }
}>()

const emit = defineEmits<{
  pause: [currentTime: number]
  seeked: [currentTime: number]
}>()

const videoPlayerRef: Ref<HTMLVideoElement | null> = ref(null)
const videoPlayer = ref<VideoPlayerType | null>(null)

const defaultOptions = {
  fluid: true,
  preload: 'auto',
  controls: true,
  html5: { nativeControlsForTouch: true },
  liveui: true,
  enableSmoothSeeking: true,
}

const playerOptions = computed(() => {
  return { ...defaultOptions, ...props.options }
})

const videojsSource = inject<ComputedRef<{ js: string; css: string }>>(
  'videojsSource',
  computed(() => ({
    js: 'https://vjs.zencdn.net/8.17.1/video.min.js',
    css: 'https://vjs.zencdn.net/8.17.1/video-js.css',
  })),
)

const viewVideo = async () => {
  let cacheHit

  if (!(window as any).videojs) {
    try {
      await loadScriptStyle({
        script: { src: videojsSource.value.js },
        css: { href: videojsSource.value.css },
      })
    } catch (error) {
      console.error(`${error}: Video.js`)
    }

    cacheHit = false
  } else {
    cacheHit = true
  }
  console.info(`[CVideo] ${cacheHit ? 'video.js cache hit!' : 'video.js loaded for the first time!'}`)
  if (!videoPlayerRef.value || !playerOptions.value.sources) {
    console.warn(`[CVideo] No video player element found!\nCurrent value: ${videoPlayerRef.value}`)

    return Promise.resolve()
  }

  const videoJs = (window as any).videojs
  if (!videoJs) {
    console.warn('[CVideo] No video lib found!')

    return Promise.resolve()
  }

  videoPlayer.value = videoJs(videoPlayerRef.value, playerOptions.value, function () {
    if (props.currentTime) videoPlayer.value!.currentTime(props.currentTime)

    videoPlayer.value!.on('pause', function () {
      const currentTime = videoPlayer.value!.currentTime()
      emit('pause', currentTime)
    })

    videoPlayer.value!.on('seeked', function () {
      const currentTime = videoPlayer.value!.currentTime()
      emit('seeked', currentTime)
    })
  })

  return Promise.resolve()
}

onMounted(async () => {
  await viewVideo()
  addSwiperIgnoreClass()
})

const addSwiperIgnoreClass = () => {
  if (videoPlayer.value?.el_) {
    videoPlayer.value?.el_.querySelector('.vjs-control-bar')?.classList.add('swiper-no-swiping')
  }
}

const preventDefault = (event: Event) => {
  event.preventDefault()
}

onBeforeUnmount(() => {
  if (videoPlayer.value) {
    videoPlayer.value.dispose()
  }
})

defineExpose({
  videoPlayer,
})
</script>

<style scoped>
.video-js :deep(.vjs-poster img),
.video-js :deep(video) {
  border-radius: 8px;
}

.video-js :deep(.vjs-poster img) {
  object-fit: v-bind('props.options?.posterBackgroundSize ?? "cover"');
}
</style>
